
import utc from "dayjs/plugin/utc";
import dayjs, { Dayjs } from "dayjs";
import { isNull, isNil, has } from "lodash";

import { DateFormats, FieldType } from "../constants/Date";

dayjs.extend(utc);

/**
 * @param {string | Date}  dateInput - A date string or date object
 * @param {string | null} [outputFormat = null] - An optional param for output format | DateFormats enum | Default is "MM/DD/YYYY h:mm a"
 * @param {string | null} [inputFormat = null] - An optional param for input format | DateFormats enum | Default is accepted formats by dayjs
 * @param {boolean | null} [isUTC = null] - An optional param if date to be formatted in UTC format only | Default is false
 *
 * @returns {string} This is the formatted date string or empty when invalid date object or string input is there
 */
export const formatDate = (
  dateInput: string | Date,
  outputFormat: string | null = null,
  inputFormat: string | null = null,
  isUTC: boolean | null = null
): string => {
  let formattedDateString = "";
  if (!isNil(dateInput)) {
    const d =
      dateInput instanceof Date || !isNull(inputFormat)
        ? dateInput
        : new Date(dateInput);
    let dateObj: dayjs.Dayjs;
    if (isNull(outputFormat)) {
      outputFormat = DateFormats.MM_DD_YYYY_h_mm_a;
    }
    if (inputFormat) {
      dateObj = dayjs(d, inputFormat);
      if (isUTC) {
        dateObj = dayjs.utc(d, inputFormat);
      }
    } else {
      dateObj = dayjs(d);
      if (isUTC) {
        dateObj = dayjs.utc(d);
      }
    }

    formattedDateString = dateObj.isValid() ? dateObj.format(outputFormat) : "";
  }

  return formattedDateString;
};

export const formatDateWithoutConversionTimezone = (dateString: string | Date, outputFormat: string) => {
  return dayjs.utc(dateString).format(outputFormat);
}

export const dateWithoutUtc = (date: string) => {
  return dayjs(date).format(DateFormats.YYYY_MM_DD)
}

/**
 * @param {any}  restData - A restData any
 *
 * @returns {string} This is the utcdate
 */
export const utcDateToUser = (restData: any) => {
  if (Array.isArray(restData)) {
    for (const item of restData) {
      utcDateToUser(item);
    }
  } else if (typeof restData === "object") {
    for (const objectKey in restData) {
      if (has(FieldType, objectKey)) {
        if (restData[objectKey]) {
          if (
            !(
              restData[objectKey].includes("z") ||
              restData[objectKey].includes("Z")
            )
          ) {
            restData[objectKey] = dayjs(restData[objectKey] + "z").toJSON();
          }
        }
      }
      utcDateToUser(restData[objectKey]);
    }
  }
  return restData;
};

/**
 * @param {string | Date}  dateInput - A date string or date object
 * @param {string} outputFormat - A date format string | DateFormats enum
 *
 * @returns {string} This is the formatted date string in local timezone or empty when invalid date object or string input is there
 */
export const formatUTCToLocal = (
  dateInput: string | Date,
  outputFormat: string
): string => {
  if (!dateInput) return "";

  const dateStr =
    typeof dateInput === "string"
      ? dateInput.endsWith("Z")
        ? dateInput
        : dateInput + "Z"
      : dateInput;

  const dateObj = dayjs.utc(dateStr).local();

  return dateObj.isValid() ? dateObj.format(outputFormat) : "";
};

export const parseMaxAllowedSocDate = (
  value?: string | null
): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(
    formatDateWithoutConversionTimezone(value, DateFormats.YYYY_MM_DD)
  );
  return parsed.isValid() ? parsed.startOf("day") : null;
};